import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * SaveCampsiteBar — sticky bottom conversion bar shown to anonymous
 * campers on `/campsites/:id` pages.
 *
 * Why this exists: the presence dashboard shows ~99% of daily traffic is
 * anonymous drive-by from Google/social straight into a detail page.
 * `SignupNudgeModal` only mounts on Landing, so most of that traffic
 * never sees a prompt. This bar puts the ask in front of them the moment
 * they've engaged with the site (small scroll offset before it appears)
 * and lets them dismiss it per-campsite so they aren't nagged.
 *
 * UX contract:
 *   - Signed-in users → nothing rendered.
 *   - Dismissed per-campsite via localStorage (`ccu:save-bar:<id>`), so
 *     the same visitor sees a fresh prompt on the next campsite they
 *     open — one dismissal ≠ blanket opt-out.
 *   - Appears after 400ms + a tiny scroll (or 6 s max wait) so it never
 *     covers the hero image on first paint.
 *   - "Save this campsite" is a concrete micro-goal, not "sign up".
 *     Every study of signup conversion says the ask must equal what
 *     the visitor loses by not signing up. Here: their favourite.
 */
const DISMISS_KEY = (id) => `ccu:save-bar:${id}`;

export default function SaveCampsiteBar({ campsiteId, campsiteName }) {
  const { isAuthenticated } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isAuthenticated || !campsiteId) return undefined;
    // Respect a prior dismiss on this exact campsite.
    try {
      if (window.localStorage.getItem(DISMISS_KEY(campsiteId)) === "1") return undefined;
    } catch { /* private mode — fail open, still show */ }
    let timer;
    let shown = false;
    const onScroll = () => {
      if (shown) return;
      if (window.scrollY > 120) {
        shown = true;
        setVisible(true);
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Fallback: show after 6s even if they don't scroll (bounce-catching).
    timer = setTimeout(() => {
      if (!shown) {
        shown = true;
        setVisible(true);
        window.removeEventListener("scroll", onScroll);
      }
    }, 6000);
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (timer) clearTimeout(timer);
    };
  }, [isAuthenticated, campsiteId]);

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY(campsiteId), "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  if (isAuthenticated || !visible || !campsiteId) return null;

  const next = `/campsites/${campsiteId}`;
  const registerHref = `/register?next=${encodeURIComponent(next)}`;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[55] pb-[env(safe-area-inset-bottom)] pointer-events-none"
      data-testid="save-campsite-bar"
    >
      <div className="mx-auto max-w-3xl m-3 sm:m-4 pointer-events-auto">
        <div className="bg-[#0e0e0e] text-white border-2 border-[#0e0e0e] shadow-[0_6px_0_0_#ff4500] flex items-stretch overflow-hidden">
          <div className="flex-1 min-w-0 px-3 py-2.5 sm:px-4 sm:py-3">
            <div className="mono text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-[#ff9260] leading-tight">
              Save your spot
            </div>
            <div className="text-sm sm:text-base font-bold leading-tight mt-0.5 truncate">
              Save {campsiteName ? `“${campsiteName}”` : "this campsite"} to your list
            </div>
            <div className="text-[10px] sm:text-[11px] text-white/75 leading-tight mt-0.5 hidden sm:block">
              Free · no card · 10-second signup.
            </div>
          </div>
          <Link
            to={registerHref}
            className="shrink-0 self-stretch flex items-center gap-1.5 bg-[#ff4500] text-white text-[11px] sm:text-xs font-black uppercase tracking-widest px-3 sm:px-4 hover:bg-[#e63e00] transition-colors"
            data-testid="save-campsite-bar-cta"
          >
            <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.25} />
            <span>Save</span>
          </Link>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="shrink-0 self-stretch px-2 sm:px-2.5 text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="save-campsite-bar-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
